import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useCampaigns } from '@/lib/hooks/useCampaign';

interface TimelineDataPoint {
  date: string;
  sent: number;
  opened: number;
  clicked: number;
  replied: number;
}

interface ReportsTimelineProps {
  data: TimelineDataPoint[];
  isLoading?: boolean;
}

export function ReportsTimeline({ data, isLoading }: ReportsTimelineProps) {
  const navigate = useNavigate();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const { createCampaign } = useCampaigns();

  if (isLoading) {
    return (
      <Card className="bg-card shadow-none border mb-6 p-6">
        <Skeleton className="h-6 w-48 mb-4" />
        <Skeleton className="h-80 w-full" />
      </Card>
    );
  }

  return (
    <Card className="bg-card shadow-none border mb-6 p-6">
      <h3 className="text-lg font-medium mb-4 text-foreground">
        Replies Over Time
      </h3>
      {data.length === 0 ? (
        <div className="h-80 flex items-center justify-center">
          <div className="text-center max-w-md">
            <h3 className="text-lg text-foreground mb-2">You have no data available</h3>
            <p className="text-sm text-foreground mb-6">Start campaigns to generate replies</p>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="primary">
                  Create a Campaign
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader className="ml-2 mt-2 mb-4">
                  <DialogTitle>Create New Campaign</DialogTitle>
                </DialogHeader>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!newCampaignName.trim()) return;

                  setIsCreating(true);
                  try {
                    const newCampaign = await createCampaign({ name: newCampaignName.trim() });
                    setNewCampaignName('');
                    setIsCreateDialogOpen(false);

                    // Navigate to the campaign editor
                    navigate(`/dashboard/campaigns/${newCampaign.id}/edit`);
                  } catch (error) {
                    console.error('Error creating campaign:', error);
                  } finally {
                    setIsCreating(false);
                  }
                }} className="space-y-4">
                  <div>
                    <Input
                      id="campaign-name"
                      value={newCampaignName}
                      onChange={(e) => setNewCampaignName(e.target.value)}
                      placeholder="Campaign title"
                      inlineLabel="Name"
                      className="w-full mt-2"
                      autoFocus
                      required
                    />
                  </div>
                  <div className="flex gap-3 pt-6 justify-end -mr-2 -mb-2">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setIsCreateDialogOpen(false)}
                      disabled={isCreating}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isCreating || !newCampaignName.trim()}
                      variant="primary"
                    >
                      {isCreating ? 'Creating...' : 'Create Campaign'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="date"
              stroke="#6b7280"
              style={{ fontSize: '0.8rem' }}
            />
            <YAxis stroke="#6b7280" style={{ fontSize: '0.8rem' }} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--popover)',
                border: '1px solid var(--line-chart)',
                borderRadius: '6px',
              }}
              labelStyle={{ color: 'var(--content-strong)' }}
            />
            <Legend
              wrapperStyle={{ fontSize: '14px' }}
              iconType="line"
            />
            <Line
              type="monotone"
              dataKey="sent"
              stroke="#3b82f6"
              name="Sent"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="clicked"
              stroke="#f59e0b"
              name="Clicked"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="replied"
              stroke="#ef4444"
              name="Replied"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}
